import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppIcon } from '../app-shell-core';

const STORAGE_KEY = 'nuramend-nds-debug';
const ISSUE_ATTRIBUTE = 'data-nds-debug-issue';

type NdsAudit = {
  nestedTables: number;
  nestedSurfaces: number;
  nonCanonicalDrawers: number;
  typographyMismatches: number;
};

type HealthLive = {
  environment?: string;
};

const EMPTY_AUDIT: NdsAudit = {
  nestedTables: 0,
  nestedSurfaces: 0,
  nonCanonicalDrawers: 0,
  typographyMismatches: 0,
};

function currentBreakpoint(): string {
  if (window.matchMedia('(max-width: 760px)').matches) return 'mobile <=760';
  if (window.matchMedia('(max-width: 1100px)').matches) return 'compact <=1100';
  return 'desktop >1100';
}

function clearDebugIssueMarkers() {
  document.querySelectorAll(`[${ISSUE_ATTRIBUTE}]`).forEach((element) => { element.removeAttribute(ISSUE_ATTRIBUTE); element.removeAttribute('data-nds-debug-role'); });
}

function mark(elements: Iterable<Element>, issue: string): number {
  let count = 0;
  for (const element of elements) {
    element.setAttribute(ISSUE_ATTRIBUTE, issue);
    count += 1;
  }
  return count;
}

function typographyRole(element: Element): { role: string; expected: number } | null {
  const explicit = element.getAttribute('data-nds-type');
  const explicitRoles: Record<string, number> = {
    'page-title': 32,
    'section-title': 18,
    body: 14,
    item: 14,
    control: 14,
    subinfo: 12.5,
    'table-label': 12.5,
    status: 12.5,
    'metric-value': 23,
  };
  if (explicit && explicitRoles[explicit] !== undefined) return { role: explicit, expected: explicitRoles[explicit] };

  if (element.matches('.app-shell-page th, .app-drawer th, .app-modal-core th')) return { role: 'table-label', expected: 12.5 };
  if (element.matches('.app-shell-page label, .app-drawer label, .app-modal-core label')) return { role: 'subinfo', expected: 12.5 };
  if (element.matches('.app-shell-page input, .app-shell-page select, .app-shell-page textarea, .app-drawer input, .app-drawer select, .app-drawer textarea, .app-modal-core input, .app-modal-core select, .app-modal-core textarea')) return { role: 'control', expected: 14 };
  if (element.matches('.muted-copy, .helper-copy, .field-help')) return { role: 'subinfo', expected: 12.5 };
  return null;
}


function auditCurrentPage(): NdsAudit {
  clearDebugIssueMarkers();

  const nestedTables = Array.from(document.querySelectorAll('.app-shell-page table table, .app-drawer table table, .app-modal-core table table'));
  const nestedSurfaceSelector = [
    '.app-shell-page .card .card',
    '.app-shell-page .nura-surface .nura-surface',
    '.app-shell-page .table-wrap .table-wrap',
    '.app-shell-page .utility-record-row .utility-record-row',
    '.app-drawer .card .card',
    '.app-drawer .nura-surface .nura-surface',
    '.app-modal-core .card .card',
    '.app-modal-core .nura-surface .nura-surface',
  ].join(', ');
  const nestedSurfaces = Array.from(document.querySelectorAll(nestedSurfaceSelector));
  const nonCanonicalDrawers = Array.from(document.querySelectorAll('.app-drawer:not(.app-drawer-size-standard):not(.app-drawer-size-wide)'));

  mark(nestedTables, 'nested-table');
  mark(nestedSurfaces, 'nested-surface');
  mark(nonCanonicalDrawers, 'drawer-variant');

  const typographyCandidates = Array.from(document.querySelectorAll('[data-nds-type], .app-shell-page :is(th, label, input, select, textarea, .muted-copy, .helper-copy, .field-help), .app-drawer :is(th, label, input, select, textarea, .muted-copy, .helper-copy, .field-help), .app-modal-core :is(th, label, input, select, textarea, .muted-copy, .helper-copy, .field-help)'));
  const typographyMismatches = typographyCandidates.filter((element) => {
    const expectation = typographyRole(element);
    if (!expectation) return false;
    const actual = Number.parseFloat(window.getComputedStyle(element).fontSize || '0');
    if (!Number.isFinite(actual) || Math.abs(actual - expectation.expected) <= 0.2) return false;
    element.setAttribute('data-nds-debug-role', `${expectation.role}:${actual}px/${expectation.expected}px`);
    return true;
  });
  mark(typographyMismatches, 'typography');

  return {
    nestedTables: nestedTables.length,
    nestedSurfaces: nestedSurfaces.length,
    nonCanonicalDrawers: nonCanonicalDrawers.length,
    typographyMismatches: typographyMismatches.length,
  };
}

export function NdsDebugControl({ authorized }: { authorized: boolean }) {
  const [environment, setEnvironment] = useState('unknown');
  const [available, setAvailable] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [breakpoint, setBreakpoint] = useState(() => typeof window === 'undefined' ? 'unknown' : currentBreakpoint());
  const [audit, setAudit] = useState<NdsAudit>(EMPTY_AUDIT);

  useEffect(() => {
    let active = true;
    void fetch('/api/v1/health/live', { credentials: 'same-origin', cache: 'no-store' })
      .then(async (response) => response.ok ? await response.json() as HealthLive : {})
      .then((health) => {
        if (!active) return;
        const runtimeEnvironment = String(health.environment || 'unknown').trim().toLowerCase();
        setEnvironment(runtimeEnvironment);
        const stagingAvailable = authorized && runtimeEnvironment === 'staging';
        setAvailable(stagingAvailable);
        if (runtimeEnvironment !== 'staging') {
          setEnabled(false);
          delete document.documentElement.dataset.ndsDebug;
          clearDebugIssueMarkers();
          return;
        }
        setEnabled(stagingAvailable && localStorage.getItem(STORAGE_KEY) === '1');
      })
      .catch(() => {
        if (!active) return;
        setEnvironment('unknown');
        setAvailable(false);
        setEnabled(false);
        delete document.documentElement.dataset.ndsDebug;
        clearDebugIssueMarkers();
      });
    return () => { active = false; };
  }, [authorized]);

  const debugActive = available && environment === 'staging' && enabled;

  const refreshAudit = useCallback(() => {
    if (!debugActive) return;
    setBreakpoint(currentBreakpoint());
    setAudit(auditCurrentPage());
  }, [debugActive]);

  useEffect(() => {
    if (!debugActive) {
      delete document.documentElement.dataset.ndsDebug;
      clearDebugIssueMarkers();
      setAudit(EMPTY_AUDIT);
      return;
    }

    document.documentElement.dataset.ndsDebug = 'true';
    refreshAudit();

    let frame = 0;
    const queueAudit = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(refreshAudit);
    };
    const observer = new MutationObserver(queueAudit);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', queueAudit);
    window.addEventListener('popstate', queueAudit);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', queueAudit);
      window.removeEventListener('popstate', queueAudit);
      window.cancelAnimationFrame(frame);
      delete document.documentElement.dataset.ndsDebug;
      clearDebugIssueMarkers();
    };
  }, [debugActive, refreshAudit]);

  const issueTotal = useMemo(() => audit.nestedTables + audit.nestedSurfaces + audit.nonCanonicalDrawers + audit.typographyMismatches, [audit]);

  if (!available || environment !== 'staging') return null;

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
  };

  return <>
    <button
      type="button"
      className={`icon-button nds-debug-toggle ${debugActive ? 'active' : ''}`.trim()}
      onClick={toggle}
      title={`NDS Debug ${debugActive ? 'on' : 'off'} (staging only)`}
      aria-label={`NDS Debug ${debugActive ? 'on' : 'off'}`}
      aria-pressed={debugActive}
    >
      <AppIcon name="activity" size={17} />
    </button>
    {debugActive && <aside className={`nds-debug-panel ${issueTotal ? 'has-issues' : 'clean'}`} aria-label="Nuramend Design System diagnostics">
      <div className="nds-debug-heading"><strong>NDS v1 Debug</strong><span>{environment}</span><button type="button" onClick={refreshAudit}>Refresh</button></div>
      <div className="nds-debug-facts">
        <span><b>Breakpoint</b>{breakpoint}</span>
        <span><b>Nested tables</b>{audit.nestedTables}</span>
        <span><b>Nested surfaces</b>{audit.nestedSurfaces}</span>
        <span><b>Drawer variants</b>{audit.nonCanonicalDrawers}</span>
        <span><b>Type mismatches</b>{audit.typographyMismatches}</span>
      </div>
      <small>{issueTotal ? `${issueTotal} NDS issue${issueTotal === 1 ? '' : 's'} highlighted on this view.` : 'No structural NDS issues detected on this view.'}</small>
    </aside>}
  </>;
}
